import { useEffect, useState } from 'react';
import { formatNumber } from 'utils/utils';
import { Button } from '../Button';
import Loader from 'components/Loader';
import RadioButton from '../RadioButton';
import TextBox from '../TextBox';
import ShoppingCartService from '../services/shopping-cart.service';
import { useErrorBoundary } from 'basket/ErrorBoundary';
import FabricIcon from 'components/FabricIcon';
import { SupplyOwnFabric } from '../SupplyOwnFabric';
import ProductQtyInput from 'components/ProductQtyInput';
import { OtherFabricSelector } from '../OtherFabricSelector';

export function BasketEditCushionsItem(props) {
    const { item, updateFabricItem, close } = props;
    const handleError = useErrorBoundary();
    const [quantity, setQuantity] = useState(item.quantity);
    const [price, setPrice] = useState(item.price);
    const [styleChanged, setStyleChanged] = useState(false);
    const convertToBoolean = (value) => value == "1"? 1 : (value == "0"? 0 : value);
    const [fabricData, setFabricData] = useState({
        cushionNameID: item.fabric.cushionNameID,
        cushionID: item.fabric.cushionID,
        pipingYesNo: item.fabric.pipingYesNo,
        featherInsertsYesNo: item.fabric.featherInsertsYesNo,
        unitOfMeasurement: item.fabric.unitOfMeasurement ?? false,
        customisationRequests: item.fabric.customisationRequests,
        fabricId: item.fabricId,
        supplyOwnFabricNameOrDescription: item.supplyOwnFabricNameOrDescription,
        supplyOwnFabricRollWidth: item.supplyOwnFabricRollWidth,
        supplyOwnFabricPatternRepeat: item.supplyOwnFabricPatternRepeat
    });
    const [loading, setLoading] = useState(false);

    const [itemTitle, setItemTitle] = useState(`${item.fabric.fabricName} ${item.colour || ''} Fabric By ${item.fabric.supplierName || ''}`);
    const title = () => `Edit Cushions In ${itemTitle}`;
    
    const [cushionStyles, setCushionStyles] = useState([]);
    const [cushionSizes, setCushionSizes] = useState([]);

    const [errors, setErrors] = useState({});
    const [isSubmitDisabled, setSubmitDisabled] = useState(false);

    const [fabrics, setFabrics] = useState([])
    const [totalFabricsCount, setTotalFabricsCount] = useState(0)
    const [selectedFabric, setSelectedFabric] = useState(fabricData.fabricId)

    const fetchStyles = async () => {
        const responseStyles = await ShoppingCartService.create('options/5', {
            unitOfMeasurement: 3,
        }).catch(error => { handleError('Could not load item details. Please contact administrator'); });
        if (responseStyles) setCushionStyles(responseStyles);
    };

    const fetchSizes = async () => {
        const responseSizes = await ShoppingCartService.create('options/5', {
            id: fabricData.cushionNameID,
            unitOfMeasurement: fabricData.unitOfMeasurement,
        }).catch(error => { handleError('Could not load item details. Please contact administrator'); });
        if (responseSizes){                
            setCushionSizes(responseSizes);
            if (styleChanged && responseSizes && responseSizes.length>0){
                setFabricData({ ...fabricData, cushionID: responseSizes[0].id });
            } 
        }
    };
    
    const handleStyleClick = (styleId) => {
        if (styleId != fabricData.cushionID){                
            setFabricData({ ...fabricData, cushionNameID: styleId });
            setStyleChanged(true);
        }
    };

    const handleSizeClick = (sizeId) => {
        setFabricData({ ...fabricData, cushionID: sizeId });
    };

    useEffect(() => {
        fetchSizes();
    }, [fabricData.cushionNameID]);

    const handleQuantityChange = (value) => {
        if (value > 0 && value <= 100) {
            setSubmitDisabled(false)
            setQuantity(value)
        }
        else {
            setSubmitDisabled(true)
        }
    }

    const handleInputChange = (e) => {        
        const { name, value } = e.target;
        const updatedData = { ...fabricData, [name]: value };
        setFabricData(updatedData);

        const validationError = validateInput(name, value);
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: validationError
        }));

        const hasErrors = Object.values({ ...errors, [name]: validationError }).some((error) => error);
        setSubmitDisabled(hasErrors);
    };

    const validateInput = (name, value) => {
        let error = '';
        const num = Number(value);
        const errorMessage = "Invalid data";

        if (name === 'supplyOwnFabricPatternRepeat' || name === 'supplyOwnFabricRollWidth') {
            if (!Number.isInteger(num) || num <= 0 || num > 999) {
                error = errorMessage;
            }
        } else if (name === 'supplyOwnFabricNameOrDescription') {
            if (!value || value.trim() === '') {
                error = "This field is required";
            }
        }
        return error;
    };

    const handleUpdate = () => {
        setLoading(true);
        updateFabricItem({
            ...item,
            fabricId: selectedFabric,
            quantity,
            fabric: { 
                ...fabricData,
                unitOfMeasurement: convertToBoolean(fabricData.unitOfMeasurement),
                pipingYesNo: convertToBoolean(fabricData.pipingYesNo),
                featherInsertsYesNo: convertToBoolean(fabricData.featherInsertsYesNo),
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.supplyOwnFabricPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.supplyOwnFabricRollWidth
        });
        setLoading(false);
    };

    const updatePrice = async () => {
        const result = await ShoppingCartService.create('price', {
            ...item,
            fabricId: selectedFabric,
            quantity,
            fabric: { 
                ...fabricData,
                fabricId: selectedFabric,
                unitOfMeasurement: convertToBoolean(fabricData.unitOfMeasurement),
                pipingYesNo: convertToBoolean(fabricData.pipingYesNo),
                featherInsertsYesNo: convertToBoolean(fabricData.featherInsertsYesNo),
            },
            supplyOwnFabricNameOrDescription: fabricData.supplyOwnFabricNameOrDescription,
            supplyOwnFabricPatternRepeat: fabricData.supplyOwnFabricPatternRepeat,
            supplyOwnFabricRollWidth: fabricData.supplyOwnFabricRollWidth
        }).catch(error => { handleError('Could not load item price. Please contact administrator'); });
        if (result) setPrice(result.price);
    }

    const loadFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId)
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    const loadAllFabrics = async () => {
        setLoading(true)
        const response = await ShoppingCartService.get('same-fabrics?fabricId=' + fabricData.fabricId + '&showAll=true')
        setFabrics(response.fabrics)
        setTotalFabricsCount(response.totalCount)
        setLoading(false)
    }

    useEffect(() => {
        updatePrice();
    }, [fabricData]);

    useEffect(() => {
        const foundFabric = fabrics.find(fabric => fabric.fabricId === selectedFabric);
        if (foundFabric){                    
            setItemTitle(foundFabric.fullName);
            updatePrice();
        }
    }, [selectedFabric]);

    useEffect(() => {
        fetchStyles()
        fetchSizes()
        loadFabrics()
    }, []);

    return (
        <Loader isLoading={loading}>
            <form className="p-32 pb-54 bg3 measurements-change active-group sm-p-20">
                <div className="row">
                    <div className="col">
                        <div className="subtitle1 mb-16 sm-text-24">{title()}</div>
                    </div>
                    <div className="col-auto">
                        <span
                            className="curtains-icon curtains-icon-close size-24 show-btn hide-btn"
                            onClick={() => close()}
                        ></span>
                    </div>
                </div>
                
                {
                    item.supplyOwnFabric && (
                        <SupplyOwnFabric fabricData={fabricData} setFabricData={setFabricData} errors={errors} handleInputChange={handleInputChange} />
                    )
                }

                <OtherFabricSelector fabrics={fabrics} totalFabricsCount={totalFabricsCount} loadAllFabrics={loadAllFabrics} selectedFabric={selectedFabric} setSelectedFabric={setSelectedFabric} />

                {/* Cushion Styles */}
                <div className="mb-16">
                    <div className="body-text3 gray mb-8">Cushion Style:</div>
                    <div className="thumbnails choice-thumbnails gap-24 fit-content sm-gap-12">
                        {cushionStyles && cushionStyles.map((style) => (
                            <div
                                key={style.id}
                                className={`choice-heading choice-cushion-style max-width-96 ${
                                    style.id === fabricData.cushionNameID ? 'active' : ''
                                }`}
                                onClick={() => handleStyleClick(style.id)}
                            >
                                <div className="thumbnail pointer mb_5 rel">
                                    <img src={style.imageUrl} className="max-height-97" alt={style.name} />
                                    <span className="curtains-icon curtains-icon-Check"></span>
                                </div>
                                <div className="body-text6 ps_5">{style.name}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <hr className="secondary-line mb-16" />

                {/* Cushion Sizes and Measurements */}
                <div>
                    <div className="body-text3 gray mb-8">Measurements:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="unitOfMeasurement"
                            label={'Cm'}
                            value={0}
                            checked={fabricData.unitOfMeasurement == 0}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="unitOfMeasurement"
                            label={'Inches'}
                            value={1}
                            checked={fabricData.unitOfMeasurement == 1}
                            onChange={handleInputChange}
                        />
                    </div>

                    {/* Cushion Sizes */}
                    <div
                        id="cushionSizes"
                        className={`${fabricData.unitOfMeasurement === 1 ? 'three-columns' : 'four-columns'} gap-8 row-gap-16 sm-gap-8`}
                    >
                        {cushionSizes && cushionSizes.map((size) => (
                            <div
                                key={size.id}
                                className={`light-accent-box pointer make-active ${size.id === fabricData.cushionID ? 'active' : ''}`}
                                onClick={() => handleSizeClick(size.id)}
                            >
                                <span className="curtains-icon curtains-icon-Check"></span> {size.name}
                            </div>
                        ))}
                    </div>
                </div>
                <hr className="secondary-line mb-16"></hr>

                {/* Piping */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8">Piping:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="pipingYesNo"
                            label={'Yes'}
                            value={1}
                            checked={fabricData.pipingYesNo == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="pipingYesNo"
                            label={'No'}
                            value={0}
                            checked={fabricData.pipingYesNo == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <div className="dashed-line mb-16"></div>

                {/* Insert Filling */}
                <div>
                    <div className="body-text3 gray mb-8">Insert Filling:</div>
                    <div className="d-flex gap-32 mb-16">
                        <RadioButton
                            name="featherInsertsYesNo"
                            label={'Feather'}
                            value={1}
                            checked={fabricData.featherInsertsYesNo == 1}
                            onChange={handleInputChange}
                        />
                        <RadioButton
                            name="featherInsertsYesNo"
                            label={'Hollow fibre'}
                            value={0}
                            checked={fabricData.featherInsertsYesNo == 0}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>
                <hr className="secondary-line"/>

                {/* Customization */}
                <div>
                    <div className="body-text3 gray mt-16 mb-8 align-center gap_3">Cushion Customisation:</div>
                    <div className="max-width-410 mb-16 sm-text-14">If you have special requests, write below or <a href="/contact-us" className="underline">contact us</a> <a href="tel:0345 8620743" className="underline">0345 8620743</a></div>
                    <TextBox
                        name="customisationRequests"
                        value={fabricData.customisationRequests}
                        onChange={handleInputChange}
                        placeholder='e.g. Trim or borders'
                    />
                </div>
                <div className="dashed-line mt-16"></div>

                {/* TOTAL */}
                <div>
                    <div className="subtitle2 mb-8 mt-16">TOTAL: <span className="body-text1">£{formatNumber(price*quantity, 2)}</span></div>
                    <div className="body-text6">Delivery for made-to-measure cushions: 2-4 weeks.</div>
                </div>

                <div className="mb-16 mt-32" id="updateProductQuantity">
                    <div className="dashed-line mb-24"></div>
                    <div className="body-text3 gray mb-16">Quantity:</div>
                    
                    <ProductQtyInput initValue={quantity} onlyInt={true} onChange={handleQuantityChange} />
                </div>

                <div className="d-flex gap-16 mb-16 sm-column sm-gap-8 sm-mb-0">
                    <div className="w-100 show-btn hide-btn">
                        <Button name="UPDATE" onClick={handleUpdate} disabled={isSubmitDisabled} />
                    </div>
                </div>
            </form>
        </Loader>
    );
}